import type {
  CounterProfileRecord,
  CycleRecord,
  DeductionCaseRecord,
  DocumentType,
  EntityRole,
  RequiredDocumentTemplateRecord,
  StocktakeComputedState,
  StocktakeRecord,
  StocktakeStepRecord,
  UploadRecord,
  WorkflowTemplate,
} from "./domain";
import { packDocumentOrder } from "./domain";

export const workflowTemplates: readonly WorkflowTemplate[] = [
  {
    stepNo: 1,
    phase: "Pre-Stocktake",
    entity: "Resort",
    action: "Extract Stock State",
    requiredDocument: "Stock State Report",
    requiredDocumentType: "stock_state",
    predecessorStepNo: null,
    dueDay: 3,
    ownerTitle: "Resort Stock Controller",
    gateCode: "stock_state_saved",
  },
  {
    stepNo: 2,
    phase: "Pre-Stocktake",
    entity: "Resort",
    action: "Extract Blind Stocktake Listing",
    requiredDocument: "Blind Stocktake Listing",
    requiredDocumentType: "blind_count",
    predecessorStepNo: 1,
    dueDay: 3,
    ownerTitle: "Resort Stock Controller",
    gateCode: "blind_listing_saved",
  },
  {
    stepNo: 3,
    phase: "Pre-Stocktake",
    entity: "Resort",
    action: "Confirm System Readiness / Radical Expenses Reviewed",
    requiredDocument: "System Readiness Check",
    predecessorStepNo: 2,
    dueDay: 3,
    ownerTitle: "Resort Management",
    gateCode: "system_ready",
  },
  {
    stepNo: 4,
    phase: "Execution",
    entity: "Resort",
    action: "First Count Completed",
    requiredDocument: "Blind Count Sheets",
    requiredDocumentType: "count_sheet_first",
    predecessorStepNo: 3,
    dueDay: 5,
    ownerTitle: "Counter 1",
    gateCode: "first_count",
  },
  {
    stepNo: 5,
    phase: "Execution",
    entity: "Resort",
    action: "Second Count Completed",
    requiredDocument: "Blind Count Sheets",
    requiredDocumentType: "count_sheet_second",
    predecessorStepNo: 4,
    dueDay: 5,
    ownerTitle: "Counter 2",
    gateCode: "second_count",
  },
  {
    stepNo: 6,
    phase: "Execution",
    entity: "Resort",
    action: "Read In on Vento by Third Party",
    requiredDocument: "Vento Capture Evidence",
    requiredDocumentType: "vento_capture_evidence",
    predecessorStepNo: 5,
    dueDay: 5,
    ownerTitle: "Independent Capturer",
    gateCode: "third_party_vento",
  },
  {
    stepNo: 7,
    phase: "Reconciliation",
    entity: "OAN",
    action: "Validate All Stores / Categories / Items Counted",
    requiredDocument: "Validation Notes",
    requiredDocumentType: "variance_report",
    predecessorStepNo: 6,
    dueDay: 7,
    ownerTitle: "OAN Reviewer",
    gateCode: "oan_reconciliation",
  },
  {
    stepNo: 8,
    phase: "Reconciliation",
    entity: "Senior Regional Manager",
    action: "Review Losses, Surpluses and Explanations",
    requiredDocument: "Variance Report + Declaration",
    requiredDocumentType: "declaration",
    predecessorStepNo: 7,
    dueDay: 10,
    ownerTitle: "Regional Manager",
    gateCode: "manager_review",
  },
  {
    stepNo: 9,
    phase: "Reconciliation",
    entity: "Senior Regional Manager",
    action: "Approve or Reject Queries / Corrections",
    requiredDocument: "Approval Record",
    predecessorStepNo: 8,
    dueDay: 10,
    ownerTitle: "Regional Manager",
    gateCode: "manager_approval",
  },
  {
    stepNo: 10,
    phase: "Accountability",
    entity: "Resort",
    action: "Issue Declaration Per Outlet",
    requiredDocument: "Declaration",
    requiredDocumentType: "declaration",
    predecessorStepNo: 9,
    dueDay: 10,
    ownerTitle: "Resort Management",
    gateCode: "declaration_issued",
  },
  {
    stepNo: 11,
    phase: "Accountability",
    entity: "Resort",
    action: "Issue Deduction Letters for Negative Variances",
    requiredDocument: "Deduction Letters",
    requiredDocumentType: "deduction_letter",
    predecessorStepNo: 10,
    dueDay: 10,
    ownerTitle: "Resort Management",
    gateCode: "deductions_issued",
  },
  {
    stepNo: 12,
    phase: "Payroll",
    entity: "Resort",
    action: "Send Wage Log + Deduction Letters to HR and CC OAN",
    requiredDocument: "Submission Email / Wage Log",
    requiredDocumentType: "wage_log",
    predecessorStepNo: 11,
    dueDay: 10,
    ownerTitle: "Resort Management",
    gateCode: "payroll_pack_sent",
  },
  {
    stepNo: 13,
    phase: "Payroll",
    entity: "OAN",
    action: "Verify Payroll Pack Matches Approved Variances",
    requiredDocument: "OAN Verification",
    predecessorStepNo: 12,
    dueDay: 13,
    ownerTitle: "OAN Reviewer",
    gateCode: "oan_payroll_verification",
  },
  {
    stepNo: 14,
    phase: "Payroll",
    entity: "HR/Payroll",
    action: "Process Approved Payroll Deductions",
    requiredDocument: "Payroll Confirmation",
    requiredDocumentType: "payroll_confirmation",
    predecessorStepNo: 13,
    dueDay: 15,
    ownerTitle: "Payroll Officer",
    gateCode: "payroll_processed",
  },
  {
    stepNo: 15,
    phase: "Finalisation",
    entity: "Resort",
    action: "Compile Final Scanned Pack in Prescribed Order",
    requiredDocument: "Final Stocktake Pack",
    predecessorStepNo: 14,
    dueDay: 15,
    ownerTitle: "Resort Stock Controller",
    gateCode: "final_pack_compiled",
  },
] as const;

export function workflowOwnerToEntityRole(owner: WorkflowTemplate["entity"]): EntityRole {
  if (owner === "Resort") {
    return "resort_ops";
  }
  if (owner === "OAN") {
    return "oan";
  }
  if (owner === "Senior Regional Manager") {
    return "regional_manager";
  }
  return "payroll";
}

export function buildDefaultRequiredDocumentTemplates(entityId: string): RequiredDocumentTemplateRecord[] {
  return packDocumentOrder.map((documentType, index) => ({
    id: `${entityId}-document-${documentType}`,
    entityId,
    documentType,
    label: documentType.replaceAll("_", " "),
    required: true,
    sortOrder: index + 1,
    notes: null,
  }));
}

export function buildDefaultCounterProfiles(entityId: string): CounterProfileRecord[] {
  return [
    {
      id: `${entityId}-counter-1`,
      entityId,
      name: "Counter 1",
      roleLabel: "Primary Counter",
      sortOrder: 1,
      distinctGroup: "count-team",
      notes: "Must be different from the Vento capturer.",
    },
    {
      id: `${entityId}-counter-2`,
      entityId,
      name: "Counter 2",
      roleLabel: "Secondary Counter",
      sortOrder: 2,
      distinctGroup: "count-team",
      notes: "Independent counter for verification.",
    },
    {
      id: `${entityId}-counter-vento`,
      entityId,
      name: "Independent Capturer",
      roleLabel: "Vento Capturer",
      sortOrder: 3,
      distinctGroup: "vento-team",
      notes: "Must be different from both counters.",
    },
  ];
}

export function computeDueDate(cycle: Pick<CycleRecord, "year" | "month">, dueDay: number): string {
  return new Date(Date.UTC(cycle.year, cycle.month - 1, dueDay)).toISOString().slice(0, 10);
}

function dateDiffDays(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();
  return Math.max(0, Math.floor((to - from) / 86_400_000));
}

export function requiresDeductionLetter(deductions: DeductionCaseRecord[]): boolean {
  return deductions.some((item) => item.amount > 0);
}

function getActiveRequiredDocuments(
  requiredDocuments: RequiredDocumentTemplateRecord[] | undefined
): DocumentType[] {
  if (!requiredDocuments || requiredDocuments.length === 0) {
    return [...packDocumentOrder];
  }
  return [...requiredDocuments]
    .filter((item) => item.required)
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((item) => item.documentType);
}

export function getMissingDocuments(
  uploads: UploadRecord[],
  deductions: DeductionCaseRecord[],
  requiredDocuments?: RequiredDocumentTemplateRecord[]
): DocumentType[] {
  const requiredTypes = getActiveRequiredDocuments(requiredDocuments);
  const uploaded = new Set(uploads.filter((item) => item.status !== "archived").map((item) => item.documentType));
  return requiredTypes.filter((documentType) => {
    if (documentType === "deduction_letter") {
      return requiresDeductionLetter(deductions) && !uploaded.has(documentType);
    }
    if (documentType === "payroll_confirmation") {
      return false;
    }
    return !uploaded.has(documentType);
  });
}

export function computeStepState(args: {
  cycle: CycleRecord;
  stocktake: StocktakeRecord;
  steps: StocktakeStepRecord[];
  uploads: UploadRecord[];
  deductions: DeductionCaseRecord[];
  requiredDocuments?: RequiredDocumentTemplateRecord[];
  todayIso?: string;
}): StocktakeComputedState {
  const { cycle, steps, uploads, deductions, requiredDocuments } = args;
  const todayIso = args.todayIso ?? new Date().toISOString().slice(0, 10);
  const completeSteps = new Set(steps.filter((step) => step.status === "complete").map((step) => step.stepNo));
  const countOne = steps.find((step) => step.stepNo === 4);
  const countTwo = steps.find((step) => step.stepNo === 5);
  const vento = steps.find((step) => step.stepNo === 6);
  const blockedStep = workflowTemplates.find(
    (template) =>
      template.predecessorStepNo !== null &&
      completeSteps.has(template.stepNo) &&
      !completeSteps.has(template.predecessorStepNo)
  );
  const overdueStep = workflowTemplates.find((template) => {
    const step = steps.find((item) => item.stepNo === template.stepNo);
    if (!step || step.status === "complete") {
      return false;
    }
    const dueDate = computeDueDate(cycle, template.dueDay);
    return dueDate < todayIso;
  });
  const missingSignoff = steps.find(
    (step) => step.status === "complete" && (!step.signOffName || !step.signOffDate)
  );
  const ventoOverlap =
    Boolean(countOne?.signOffName) &&
    Boolean(countTwo?.signOffName) &&
    Boolean(vento?.signOffName) &&
    [countOne?.signOffName, countTwo?.signOffName].includes(vento?.signOffName);
  const missingDocuments = getMissingDocuments(uploads, deductions, requiredDocuments);
  const payrollReady =
    completeSteps.has(9) &&
    completeSteps.has(13) &&
    missingDocuments.length === 0 &&
    !ventoOverlap;
  const finalPackComplete = missingDocuments.length === 0 && completeSteps.has(15);
  const activeTemplate = workflowTemplates.find((template) => !completeSteps.has(template.stepNo));
  const referenceTemplate = activeTemplate ?? workflowTemplates[workflowTemplates.length - 1];
  const dueDate = computeDueDate(cycle, referenceTemplate.dueDay);

  let exceptionFlag = "";
  if (blockedStep) {
    exceptionFlag = "Completed before predecessor";
  } else if (overdueStep) {
    exceptionFlag = "Overdue";
  } else if (missingSignoff) {
    exceptionFlag = "Missing sign-off";
  } else if (ventoOverlap) {
    exceptionFlag = "Vento capture name overlaps with count";
  } else if (missingDocuments.length > 0 && args.stocktake.readOnly === false) {
    exceptionFlag = `Missing ${missingDocuments.length} required document${missingDocuments.length === 1 ? "" : "s"}`;
  }

  return {
    predecessorComplete: !blockedStep,
    canProceed: !blockedStep && !missingSignoff,
    daysLate: activeTemplate ? dateDiffDays(dueDate, todayIso) : 0,
    exceptionFlag,
    payrollReady,
    finalPackComplete,
    missingDocuments,
  };
}
